import SpaceBetween from '@risk-smart/themed-cloudscape-components/space-between';
import Button from '@risksmart-app/components/src/button';
import { PageNotFound } from '@risksmart-app/components/src/errors/errors';
import { useGetGuidParam } from '@risksmart-app/components/src/routes/routes.utils';
import { Parent_Type_Enum } from '@risksmart-app/web-graphql-client/generated/graphql';
import type { FC } from 'react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import type { ObjectToolbarConfig } from 'src/blocks/object-toolbar';
import { ObjectToolbar } from 'src/blocks/object-toolbar';
import { useObjectToolbarFormStore } from 'src/blocks/object-toolbar/useObjectToolbarFormStore';
import ControlledTabs from 'src/components/controlled-tabs';
import DeleteModal from 'src/components/delete-modal/DeleteModal';
import TabSettingsModal from 'src/components/tab-settings-modal';
import { PageLayout } from 'src/layouts';
import { Permission } from 'src/rbac/Permission';
import {
  useGetDetailParentPath,
  useGetDetailPath,
} from 'src/routes/useGetDetailParentPath';

import { useDeleteIndicators } from '@/hooks/mutations/indicator/useDeleteIndicators';
import { useGetIndicatorById } from '@/hooks/queries';
import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { useDeleteResultNotification } from '@/hooks/useMutationResultNotification';
import useTabs from '@/hooks/useTabs';
import { getFriendlyId } from '@/utils/friendlyId';

import useExporter from './useExporter';

type Props = {
  activeTabId: 'details' | 'linkedItems' | 'results' | 'notificationHistory';
};

const Page: FC<Props> = ({ activeTabId }) => {
  const indicatorId = useGetGuidParam('indicatorId');
  const [exportItem, { loading: exporting }] = useExporter(indicatorId);
  const navigate = useNavigate();
  const { t } = useTranslation(['common']);
  const objectToolbarEnabled = useIsFeatureFlagEnabled('object_toolbar');

  const parentPath = useGetDetailParentPath(indicatorId);
  const detailPath = useGetDetailPath(indicatorId);

  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [showTabSettings, setShowTabSettings] = useState(false);
  // Form-editor opener bridged up from the active tab's FormMenu (relocate mode).
  // Present only on the details tab when object_toolbar + form_sections are on.
  const formConfig = useObjectToolbarFormStore((s) => s.formConfigState);
  const { t: st } = useTranslation(['common'], { keyPrefix: 'indicators' });
  const defaultTitle = st('fallback_title');

  const { data, error } = useGetIndicatorById({
    queryArgs: { id: indicatorId },
    shouldSkip: !indicatorId,
  });
  if (error) {
    throw error;
  }

  const indicator = data?.indicator?.[0];
  const tabs = useTabs({
    parentType: Parent_Type_Enum.Indicator,
    parent: indicator,
    hrefRoot: detailPath,
  });
  const { deleteIndicators, loading: deleteLoading } = useDeleteIndicators();

  const onDelete = useDeleteResultNotification({
    asyncAction: async () => {
      if (!indicator) {
        throw new Error('Indicator not selected');
      }
      await deleteIndicators([indicator.Id]);
      navigate(parentPath);
      setIsDeleteModalVisible(false);

      return true;
    },
    entityName: st('entity_name'),
    failureAction: () => {
      setIsDeleteModalVisible(false);
    },
  });

  if (data?.indicator.length === 0) {
    throw new PageNotFound(`Indicator with id ${indicatorId} not found`);
  }
  const friendlyId =
    indicator &&
    getFriendlyId(Parent_Type_Enum.Indicator, indicator.SequentialId);
  const counter = friendlyId && `(${friendlyId})`;

  // Object-level toolbar pilot (CUS-135) — flag-gated. Relocates Export + Delete
  // into the toolbar's overflow ⋮ menu and surfaces the Save disc / Cancel X
  // (save state bridged from the active tab's form via useObjectToolbarFormStore).
  const toolbarConfig: ObjectToolbarConfig = useMemo(
    () => ({
      header: {
        title: indicator?.Title ?? defaultTitle,
        // Pass the already-parenthesised counter (the toolbar renders it verbatim).
        counter: counter || undefined,
      },
      parentObject: indicator,
      save: true,
      actions: {
        overflow: [
          {
            id: 'edit',
            label: t('edit'),
            actions: [
              ...(formConfig
                ? [
                    {
                      id: 'edit-form',
                      label: t('objectToolbar.formSettings'),
                      iconName: 'file-06' as const,
                      onClick: formConfig.openConfigure,
                    },
                  ]
                : []),
              {
                id: 'edit-tabs',
                label: t('objectToolbar.tabSettings'),
                iconName: 'layers-three-01',
                onClick: () => setShowTabSettings(true),
              },
            ],
          },
        ],
        export: [
          {
            id: 'export',
            actions: [
              {
                id: 'export',
                label: t('export.standard_pdf'),
                iconName: 'download-01',
                onClick: exportItem,
                disabled: exporting,
              },
            ],
          },
        ],
        danger: [
          {
            id: 'delete',
            // Title-case "Delete Indicator" — parity with actions/risks danger
            // labels (delete_object_action rendered sentence-case "Delete
            // indicator").
            label: st('delete_button'),
            iconName: 'trash-01',
            permission: 'delete:indicator',
            onClick: () => setIsDeleteModalVisible(true),
          },
        ],
      },
    }),
    [indicator, defaultTitle, counter, t, st, exportItem, exporting, formConfig]
  );

  return (
    <PageLayout
      title={indicator?.Title ?? defaultTitle}
      meta={{
        title: defaultTitle,
      }}
      counter={counter}
      headerOverride={
        objectToolbarEnabled ? (
          <ObjectToolbar config={toolbarConfig} />
        ) : undefined
      }
      actions={
        <SpaceBetween direction={'horizontal'} size={'xs'}>
          <Button
            iconName={'download'}
            disabled={exporting}
            onClick={exportItem}
          >
            {t('export.export')}
          </Button>
          <Permission permission={'delete:indicator'} parentObject={indicator}>
            <Button
              variant={'normal'}
              formAction={'none'}
              onClick={() => {
                setIsDeleteModalVisible(true);
              }}
            >
              {st('delete_title')}
            </Button>
          </Permission>
        </SpaceBetween>
      }
    >
      <ControlledTabs
        activeTabId={activeTabId}
        tabs={tabs}
        variant={'container'}
        parentType={Parent_Type_Enum.Indicator}
        parent={indicator}
        disableSettings={objectToolbarEnabled}
      />
      <TabSettingsModal
        parentType={Parent_Type_Enum.Indicator}
        isVisible={showTabSettings}
        onDismiss={() => setShowTabSettings(false)}
        parent={indicator}
      />
      <DeleteModal
        loading={deleteLoading}
        isVisible={isDeleteModalVisible}
        header={st('delete_title')}
        onDelete={onDelete}
        onDismiss={() => setIsDeleteModalVisible(false)}
      >
        {st('confirm_delete_message')}
      </DeleteModal>
    </PageLayout>
  );
};

export default Page;
